// ============================================
// Admin Layout — Premium Design
// Matches hyper-glassmorphic pattern exactly
// Theme: Indigo/Rose
// ============================================

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Sidebar from '../common/Sidebar';
import { adminMenuItems } from '../../pages/admin/adminNav';
import NotificationBell from '../common/NotificationBell';
import ProfileMenu from '../common/ProfileMenu';
import { useTheme } from '../../context/ThemeContext';

const AdminLayout = ({ children, title, subtitle, searchTerm, onSearchChange, actions, hideSearch }) => {
    const { user } = useSelector((state) => state.auth);
    const { theme, toggleTheme } = useTheme();
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <div className="flex h-[100dvh] bg-slate-50 dark:bg-[#050510] text-slate-900 dark:text-white font-sans transition-colors duration-500 aurora-bg relative">
            {/* Sidebar — reuses shared component */}
            <Sidebar items={adminMenuItems} theme="indigo" mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Top Bar — matches pattern */}
                <header className="h-16 sm:h-24 bg-white/40 dark:bg-slate-900/40 backdrop-blur-3xl flex items-center justify-between px-4 sm:px-8 sticky top-0 z-50 border-b border-white/60 dark:border-white/5 shadow-sm dark:shadow-2xl transition-colors duration-500 gap-2 shrink-0">
                    <div className="flex items-center gap-3 sm:gap-4 shrink-0">
                        {/* Mobile Hamburger */}
                        <button 
                            onClick={() => setMobileOpen(true)}
                            className="sm:hidden p-2 -ml-2 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16m-7 6h7" />
                            </svg>
                        </button>
                        {/* Status Indicator */}
                        <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-rose-50 border border-rose-100 dark:bg-rose-500/10 dark:border-rose-500/20">
                            <div className="w-2 h-2 rounded-full bg-rose-500 animate-[pulse_2s_ease-in-out_infinite]" />
                            <span className="text-[10px] font-bold text-rose-700 dark:text-rose-400 tracking-wider">ROOT</span>
                        </div>
                        <div className="hidden sm:block">
                            <h2 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight">{title || 'Admin Center'}</h2>
                            {subtitle && <p className="text-xs text-slate-500 dark:text-slate-400 tracking-wide">{subtitle}</p>}
                        </div>
                    </div>
                    <div className="flex items-center gap-3 sm:gap-4 shrink-0">
                        {/* Dynamic Actions Passed in from page */}
                        {actions && (
                            <div className="hidden lg:flex items-center gap-2">
                                {actions}
                            </div>
                        )}

                        {/* Search */}
                        {!hideSearch && onSearchChange && (
                        <div className="hidden md:flex items-center gap-2 px-4 py-2 rounded-xl bg-white/50 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/60 w-56 lg:w-64 focus-within:border-indigo-400 dark:focus-within:border-indigo-500/50 focus-within:ring-2 focus-within:ring-indigo-500/10 transition-all backdrop-blur-sm shadow-inner group">
                            <svg className="w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input 
                                type="text" 
                                placeholder="Search here..." 
                                value={searchTerm || ''}
                                onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                                className="bg-transparent text-sm text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none flex-1 w-full min-w-0" 
                            />
                        </div>
                        )}

                        {/* Theme Toggle Button */}
                        <button 
                            onClick={toggleTheme} 
                            className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 transition-all border border-slate-200/60 active:scale-95 dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-700 dark:hover:text-indigo-400 shrink-0"
                            title="Toggle Light/Dark Mode"
                        >
                            {theme === 'dark' ? (
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 mx-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                            ) : (
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 mx-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
                            )}
                        </button>

                        {/* Notification Bell */}
                        <NotificationBell />
                        
                        {/* Avatar */}
                        <ProfileMenu user={user} themeColor="indigo" />
                    </div>
                </header>

                {/* Main Content Area */}
                <main className="flex-1 overflow-y-auto p-4 sm:p-8 bg-transparent sidebar-scroll transition-colors duration-500">
                    {/* Render mobile actions directly under header if any exist */}
                    {actions && (
                        <div className="lg:hidden flex items-center justify-end gap-2 mb-4 shrink-0">
                            {actions}
                        </div>
                    )}
                    {children}
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
